import { useEffect } from "react";
import Tag from "@/components/base/Tag";
import type { NewsItem } from "@/mocks/news";

interface NewsModalProps {
  item: NewsItem | null;
  onClose: () => void;
}

export default function NewsModal({ item, onClose }: NewsModalProps) {
  useEffect(() => {
    if (!item) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [item, onClose]);

  if (!item) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={item.title}
      className="fixed inset-0 z-[60] flex items-start justify-center overflow-y-auto bg-foreground-950/60 p-4 py-10 md:p-8"
      onClick={onClose}
    >
      <article
        className="animate-fade-up relative w-full max-w-3xl overflow-hidden rounded-lg bg-background-50"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="关闭资讯详情"
          className="absolute right-4 top-4 z-10 flex h-9 w-9 items-center justify-center rounded-full bg-foreground-950/50 text-lg text-background-50 transition-colors hover:bg-foreground-950/75"
        >
          <i className="ri-close-line" aria-hidden="true" />
        </button>

        <div className="relative h-56 w-full md:h-72">
          <img
            src={item.image}
            alt={`${item.title} - 佐佐木明希资讯配图`}
            title={`${item.title} 佐佐木明希`}
            className="h-full w-full object-cover object-top"
          />
        </div>

        <div className="p-6 md:p-8">
          <div className="flex flex-wrap items-center gap-2">
            <Tag tone="primary">{item.type}</Tag>
            <span className="text-xs text-foreground-500">{item.date}</span>
          </div>
          <h2 className="mt-4 font-heading text-xl font-bold leading-snug text-foreground-950 md:text-2xl">
            {item.title}
          </h2>
          <div className="mt-5 space-y-4">
            {item.content.map((paragraph, index) => (
              <p key={index} className="text-sm leading-loose text-foreground-700 md:text-base">
                {paragraph}
              </p>
            ))}
          </div>
          <div className="mt-6 flex flex-wrap gap-2 border-t border-background-200 pt-5">
            {item.tags.map((tag) => (
              <Tag key={tag} tone="secondary">
                #{tag}
              </Tag>
            ))}
          </div>
        </div>
      </article>
    </div>
  );
}